import { useEffect } from 'react'
import { useBoolean, useWindowView } from 'hooks'

import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import makeStyles from '@material-ui/styles/makeStyles'

import { SpacedGroup } from 'UI/Components'

import { openLink } from 'modules'
import { CallToActionType } from 'app/types'

const positions = {
    bottomLeft: {
        bottom: '0',
        left: '0'
    },
    bottomRight: {
        bottom: '0',
        right: '0'
    },
    topRight: {
        top: '0',
        right: '75px'
    },
    topLeft: {
        top: '0',
        left: '0'
    }
} as const

const mobilePosition = {
    top: '10px',
    right: '5px'
}

const useStyles = makeStyles({
    cta: {
        cursor: 'pointer',
        position: 'absolute',
        height: '50px',
        maxWidth: 'max(300px, 40%)',
        zIndex: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        border: '1px solid #E0E0E0',
        boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.25)',
        transition: 'opacity 0.3s linear',
        borderRadius: '4px',
        '&:hover': {
            boxShadow: '0px 0px 8px 4px rgba(0, 0, 0, 0.25)'
        }
    },
    image: {
        height: '40px'
    },
    text: {
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%'
    }
})

type Props = {
    cta?: CallToActionType
    videoElement: HTMLVideoElement | null
    onClick: () => void
    duration?: number
}

export const CallToAction = ({ cta, duration = 0, videoElement, onClick }: Props) => {
    const classes = useStyles()

    const { isMobileView } = useWindowView()

    const { image, text, link = '', start = 0 } = cta || {}

    const end = cta?.end || duration
    const imageLoaded = useBoolean()
    const showCta = useBoolean()
    const setShowCta = showCta.set

    useEffect(() => {
        if (!videoElement) return

        const handleShowCta = (e: Event) => {
            const time = (e.target as HTMLVideoElement).currentTime

            if (time >= start && time <= end) {
                setShowCta(true)
                return
            }
            setShowCta(false)
        }

        videoElement.addEventListener('timeupdate', handleShowCta)
        return () => videoElement.removeEventListener('play', handleShowCta)
    }, [end, setShowCta, start, videoElement])

    if (showCta.isFalse || !cta?.enabled) return null

    const positionStyles = isMobileView ? mobilePosition : positions[cta?.position || 'bottomRight']

    return (
        <Box
            className={classes.cta}
            m={1}
            p={0.5}
            pr={1}
            onClick={() => {
                openLink(link)
                onClick?.()
            }}
            title={link}
            style={{
                opacity: imageLoaded.isTrue ? 1 : 0,
                ...positionStyles
            }}
        >
            <SpacedGroup spacing={1} width="100%">
                {image && (
                    <img
                        src={image}
                        className={classes.image}
                        alt="cta"
                        onLoad={imageLoaded.setTrue}
                    />
                )}
                <Typography variant="body1" className={classes.text}>
                    {text?.label}
                </Typography>
            </SpacedGroup>
        </Box>
    )
}
